import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const Resources = () => {
  return (
    <>
      <Helmet>
        <title>Resources - Ursus Victor LLC | Loan Application Guides & Tips</title>
        <meta name="description" content="Helpful resources, guides, and tips for preparing loan applications and organizing financial documentation." />
      </Helmet>

      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
        color: 'white',
        padding: '5rem 2rem',
        textAlign: 'center'
      }}>
        <div style={{maxWidth: '800px', margin: '0 auto'}}>
          <h1 style={{fontSize: '3.5rem', fontWeight: '900', marginBottom: '1rem'}}>
            Helpful Resources
          </h1>
          <p style={{fontSize: '1.3rem', opacity: 0.95, lineHeight: '1.6'}}>
            Guides, tips, and information to help you prepare your loan application
          </p>
        </div>
      </section>

      {/* Featured Resource */}
      <section style={{padding: '5rem 2rem', background: 'white'}}>
        <div style={{maxWidth: '1100px', margin: '0 auto'}}>
          <div style={{
            background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
            padding: '3rem',
            borderRadius: '20px',
            border: '3px solid #3b82f6',
            display: 'grid',
            gridTemplateColumns: '1fr 2fr',
            gap: '3rem',
            alignItems: 'center'
          }}>
            <div style={{textAlign: 'center'}}>
              <div style={{fontSize: '6rem', marginBottom: '1rem'}}>📚</div>
              <h3 style={{fontSize: '1.8rem', fontWeight: '900', color: '#1e40af'}}>
                Featured Guide
              </h3>
            </div>
            <div>
              <h2 style={{fontSize: '2.2rem', fontWeight: '900', color: '#1e3a8a', marginBottom: '1rem'}}>
                Complete Loan Application Guide
              </h2>
              <p style={{fontSize: '1.15rem', color: '#475569', lineHeight: '1.8', marginBottom: '1.5rem'}}>
                A comprehensive guide covering everything you need to know about preparing a loan application, 
                from gathering documents to submitting your application.
              </p>
              <Link to="/resources/loan-application-guide" style={{
                background: '#1e40af',
                color: 'white',
                padding: '1rem 2.5rem',
                borderRadius: '12px',
                fontWeight: '800',
                fontSize: '1.1rem',
                display: 'inline-block'
              }}>
                Read the Guide →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Resource Categories */}
      <section style={{padding: '5rem 2rem', background: '#f8fafc'}}>
        <div style={{maxWidth: '1100px', margin: '0 auto'}}>
          <div style={{textAlign: 'center', marginBottom: '4rem'}}>
            <h2 style={{fontSize: '2.8rem', fontWeight: '900', color: '#1e3a8a', marginBottom: '1rem'}}>
              Resource Library
            </h2>
            <p style={{fontSize: '1.2rem', color: '#64748b'}}>
              Helpful information organized by topic
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '2.5rem'
          }}>
            {/* Document Checklists */}
            <div style={{
              background: 'white',
              padding: '2.5rem',
              borderRadius: '16px',
              border: '2px solid #e2e8f0'
            }}>
              <div style={{fontSize: '3.5rem', marginBottom: '1.5rem'}}>📋</div>
              <h3 style={{fontSize: '1.8rem', fontWeight: '800', color: '#1e40af', marginBottom: '1rem'}}>
                Document Checklists
              </h3>
              <p style={{color: '#64748b', lineHeight: '1.8', marginBottom: '1.5rem'}}>
                Comprehensive checklists of documents needed for different loan types
              </p>
              <ul style={{color: '#475569', lineHeight: '2', marginLeft: '1.5rem', marginBottom: '1.5rem'}}>
                <li>Personal Loan Documents</li>
                <li>Business Loan Documents</li>
                <li>Mortgage Documents</li>
                <li>Auto Loan Documents</li>
              </ul>
            </div>

            {/* Application Tips */}
            <div style={{
              background: 'white',
              padding: '2.5rem',
              borderRadius: '16px',
              border: '2px solid #e2e8f0'
            }}>
              <div style={{fontSize: '3.5rem', marginBottom: '1.5rem'}}>💡</div>
              <h3 style={{fontSize: '1.8rem', fontWeight: '800', color: '#1e40af', marginBottom: '1rem'}}>
                Application Tips
              </h3>
              <p style={{color: '#64748b', lineHeight: '1.8', marginBottom: '1.5rem'}}>
                Expert tips for preparing strong loan applications
              </p>
              <ul style={{color: '#475569', lineHeight: '2', marginLeft: '1.5rem', marginBottom: '1.5rem'}}>
                <li>Common Application Mistakes</li>
                <li>How to Improve Your Application</li>
                <li>Understanding Credit Requirements</li>
                <li>Debt-to-Income Ratio Explained</li>
              </ul>
            </div>

            {/* Document Preparation */}
            <div style={{
              background: 'white',
              padding: '2.5rem',
              borderRadius: '16px',
              border: '2px solid #e2e8f0'
            }}>
              <div style={{fontSize: '3.5rem', marginBottom: '1.5rem'}}>📄</div>
              <h3 style={{fontSize: '1.8rem', fontWeight: '800', color: '#1e40af', marginBottom: '1rem'}}>
                Document Preparation
              </h3>
              <p style={{color: '#64748b', lineHeight: '1.8', marginBottom: '1.5rem'}}>
                Guides for organizing and preparing your financial documents
              </p>
              <ul style={{color: '#475569', lineHeight: '2', marginLeft: '1.5rem', marginBottom: '1.5rem'}}>
                <li>Organizing Financial Statements</li>
                <li>Tax Return Preparation</li>
                <li>Bank Statement Guidelines</li>
                <li>Employment Verification</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Tips */}
      <section style={{padding: '5rem 2rem', background: 'white'}}>
        <div style={{maxWidth: '1000px', margin: '0 auto'}}>
          <div style={{textAlign: 'center', marginBottom: '4rem'}}>
            <h2 style={{fontSize: '2.8rem', fontWeight: '900', color: '#1e3a8a', marginBottom: '1rem'}}>
              Quick Application Tips
            </h2>
          </div>

          <div style={{display: 'grid', gap: '2rem'}}>
            {[
              {
                icon: '✓',
                title: 'Start Early',
                desc: 'Begin gathering documents well before you need them. Some documents take time to obtain.'
              },
              {
                icon: '✓',
                title: 'Be Accurate',
                desc: 'Double-check all information for accuracy. Errors can delay processing or lead to denial.'
              },
              {
                icon: '✓',
                title: 'Stay Organized',
                desc: 'Keep all documents in one place and clearly labeled. Organization speeds up the process.'
              },
              {
                icon: '✓',
                title: 'Provide Complete Information',
                desc: 'Submit all requested documents at once. Incomplete applications cause delays.'
              },
              {
                icon: '✓',
                title: 'Explain Issues',
                desc: 'If you have credit issues or gaps in employment, provide clear explanations.'
              },
              {
                icon: '✓',
                title: 'Keep Copies',
                desc: 'Always keep copies of everything you submit for your records.'
              }
            ].map((tip, index) => (
              <div key={index} style={{
                background: '#f8fafc',
                padding: '2rem',
                borderRadius: '12px',
                border: '2px solid #e2e8f0',
                display: 'flex',
                gap: '1.5rem',
                alignItems: 'flex-start'
              }}>
                <div style={{
                  background: '#3b82f6',
                  color: 'white',
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  fontWeight: '900',
                  flexShrink: 0
                }}>
                  {tip.icon}
                </div>
                <div>
                  <h3 style={{fontSize: '1.5rem', fontWeight: '800', color: '#1e40af', marginBottom: '0.5rem'}}>
                    {tip.title}
                  </h3>
                  <p style={{color: '#64748b', lineHeight: '1.7', fontSize: '1.05rem'}}>
                    {tip.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Documents */}
      <section style={{padding: '5rem 2rem', background: '#f8fafc'}}>
        <div style={{maxWidth: '1000px', margin: '0 auto'}}>
          <div style={{textAlign: 'center', marginBottom: '4rem'}}>
            <h2 style={{fontSize: '2.8rem', fontWeight: '900', color: '#1e3a8a', marginBottom: '1rem'}}>
              Commonly Required Documents
            </h2>
            <p style={{fontSize: '1.2rem', color: '#64748b'}}>
              Documents typically needed for most loan applications
            </p>
          </div>

          <div style={{
            background: 'white',
            padding: '3rem',
            borderRadius: '16px',
            border: '2px solid #e2e8f0'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1.5rem'
            }}>
              {[
                'Government-Issued ID',
                'Social Security Card',
                'Proof of Income',
                'Pay Stubs (Recent)',
                'Tax Returns (2 years)',
                'Bank Statements (3 months)',
                'Employment Verification',
                'Credit Report',
                'Proof of Address',
                'Asset Documentation',
                'Debt Information',
                'Business Documents (if applicable)'
              ].map((doc, index) => (
                <div key={index} style={{
                  padding: '1rem',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <span style={{color: '#3b82f6', fontSize: '1.5rem', fontWeight: '900'}}>✓</span>
                  <span style={{color: '#475569', fontWeight: '600'}}>{doc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #3b82f6 100%)',
        color: 'white',
        padding: '5rem 2rem',
        textAlign: 'center'
      }}>
        <div style={{maxWidth: '800px', margin: '0 auto'}}>
          <h2 style={{fontSize: '2.8rem', fontWeight: '900', marginBottom: '1.5rem'}}>
            Need Help With Your Application?
          </h2>
          <p style={{fontSize: '1.3rem', marginBottom: '2.5rem', opacity: 0.95, lineHeight: '1.6'}}>
            Let our experts guide you through the loan application process
          </p>
          <Link to="/contact" style={{
            background: 'white',
            color: '#1e40af',
            padding: '1.5rem 3.5rem',
            borderRadius: '12px',
            fontWeight: '900',
            fontSize: '1.3rem',
            display: 'inline-block',
            boxShadow: '0 12px 40px rgba(0,0,0,0.3)'
          }}>
            Get Application Support
          </Link>
        </div>
      </section>
    </>
  );
};

export default Resources;
